/**
 * PendingDocumentRow — single document line inside a bucket.
 *
 * Drag handle starts a drag with custom MIME `application/x-pending-doc-id`
 * so bucket headers can accept these drops alongside file drops without
 * a new dependency.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconFile,
  IconGripVertical,
  IconMail,
  IconPlugConnected,
  IconRadar,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react"
import clsx from "clsx"
import type { DragEvent } from "react"
import { api } from "../../../api/client"
import type { PendingDocumentOut } from "../../../types"

export const DOC_DRAG_MIME = "application/x-pending-doc-id"

const SOURCE_ICONS = {
  upload: IconUpload,
  webhook: IconPlugConnected,
  email: IconMail,
  monitor: IconRadar,
} as const

const SOURCE_LABEL_KEYS = {
  upload: "QMS/SUPPLIER/SOURCE_UPLOAD",
  webhook: "QMS/SUPPLIER/SOURCE_WEBHOOK",
  email: "QMS/SUPPLIER/SOURCE_EMAIL",
  monitor: "QMS/SUPPLIER/SOURCE_MONITOR",
} as const

interface Props {
  doc: PendingDocumentOut
  downloadUrl: string
  onDelete: () => void
  isNew?: boolean
}

export function PendingDocumentRow({
  doc,
  downloadUrl,
  onDelete,
  isNew,
}: Props) {
  const { translate } = useTranslation()
  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData(DOC_DRAG_MIME, doc.id)
    e.dataTransfer.effectAllowed = "move"
  }

  const SourceIcon = SOURCE_ICONS[doc.source] ?? IconFile
  const sizeKb = (doc.size_bytes / 1024).toFixed(1)
  const date = new Date(doc.uploaded_at).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={clsx(
        "group flex cursor-grab items-center gap-2 border-t border-grey4/30 px-4 py-2 active:cursor-grabbing",
        isNew
          ? "bg-yellow1/[0.07] hover:bg-yellow1/12"
          : "hover:bg-grey3/40",
      )}
    >
      <IconGripVertical
        size={14}
        className="shrink-0 text-grey5 opacity-50 group-hover:opacity-100"
      />
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 truncate text-left text-sm text-white hover:text-yellow1"
        title={doc.filename}
        onClick={async (e) => {
          e.stopPropagation()
          // Extract apId from the download URL pattern /ap/{apId}/documents/...
          const m = downloadUrl.match(/\/ap\/([^/]+)\/documents\/([^/]+)\//)
          if (m) {
            try {
              await api.openDocument(m[1], m[2])
              return
            } catch {
              /* fall through */
            }
          }
          window.open(downloadUrl, "_blank", "noopener")
        }}
      >
        <IconFile size={14} className="shrink-0 text-grey5" />
        <span className="truncate">{doc.filename}</span>
        {isNew && (
          <span className="shrink-0 rounded-full bg-yellow1/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-yellow1">
            {translate("QMS/SUPPLIER/BADGE_NEW")}
          </span>
        )}
      </button>
      <span className="hidden shrink-0 text-[11px] text-grey5 sm:inline">
        {sizeKb} KB
      </span>
      <span className="hidden shrink-0 items-center gap-1 text-[11px] text-grey5 md:inline-flex">
        <SourceIcon size={12} />
        {SOURCE_LABEL_KEYS[doc.source]
          ? translate(SOURCE_LABEL_KEYS[doc.source])
          : doc.source}
      </span>
      <span className="hidden shrink-0 text-[11px] text-grey5 lg:inline">
        {date}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        className="shrink-0 rounded p-1 text-grey5 hover:bg-grey3 hover:text-red1"
        title={translate("QMS/COMMON/DELETE")}
      >
        <IconTrash size={14} />
      </button>
    </div>
  )
}
